import { useEffect, useState } from "react";
import { toast } from "sonner";
import Card from "./Card";

/** Admin-only: verified access requests awaiting approval. */
export default function AdminRequests({ lang, authAxios }) {
  const [regRequests, setRegRequests] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    authAxios().get("/admin/registration-requests?status=pending")
      .then((r) => setRegRequests(r.data))
      .catch((err) => console.error("[portal] registration requests load failed:", err));
  }, [authAxios]);

  const approveRequest = async (id) => {
    if (!window.confirm(lang === "it" ? "Approvare la richiesta? Il professionista riceverà una email per impostare la password." : "Approve this request? The professional will receive an email to set a password.")) return;
    setBusy(true);
    try {
      const { data } = await authAxios().post(`/admin/registration-requests/${id}/approve`);
      setRegRequests(regRequests.filter((r) => r.id !== id));
      toast.success(data.email_sent
        ? (lang === "it" ? "Approvata: email per la password inviata" : "Approved: password email sent")
        : (lang === "it" ? "Approvata, ma l'email non è partita" : "Approved, but the email was not sent"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const rejectRequest = async (id) => {
    if (!window.confirm(lang === "it" ? "Rifiutare questa richiesta?" : "Reject this request?")) return;
    setBusy(true);
    try {
      await authAxios().post(`/admin/registration-requests/${id}/reject`);
      setRegRequests(regRequests.filter((r) => r.id !== id));
      toast.success(lang === "it" ? "Richiesta rifiutata" : "Request rejected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="mb-6" data-testid="admin-registration-section">
        <Card
          id="admin-requests"
          title={lang === "it" ? "Richieste di accesso" : "Access requests"}
          right={
            <span className="text-[12px] text-[#6e6e73]">
              {regRequests.length} {lang === "it" ? "in attesa" : "pending"}
            </span>
          }
        >
          {regRequests.length === 0 ? (
            <div className="py-8 text-center text-[13px] text-[#6e6e73]" data-testid="admin-no-requests">
              {lang === "it" ? "Nessuna richiesta in attesa." : "No pending requests."}
            </div>
          ) : (
            <div className="space-y-3">
              {regRequests.map((r) => (
                <div
                  key={r.id}
                  className="border border-[#e4e4e7] rounded-xl p-4 md:p-5 bg-[#fafafa]"
                  data-testid={`admin-request-${r.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[15px] font-semibold text-[#1d1d1f]">{r.ragione_sociale}</div>
                      <div className="mt-0.5 text-[12px] text-[#6e6e73]">
                        P.IVA {r.partita_iva}{r.settore ? ` · ${r.settore.replace(/_/g, " ")}` : ""}
                      </div>
                    </div>
                    <div className="text-[11px] text-[#6e6e73] shrink-0">
                      {new Date(r.created_at).toLocaleString(lang === "it" ? "it-IT" : "en-US")}
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-[13px] text-[#1d1d1f]">
                    <div><span className="text-[#6e6e73]">{lang === "it" ? "Referente" : "Contact"}:</span> {r.referente_nome} {r.referente_cognome}{r.referente_ruolo ? ` — ${r.referente_ruolo}` : ""}</div>
                    <div><span className="text-[#6e6e73]">Email:</span> <a href={`mailto:${r.email}`} className="text-[#1D3F94] hover:underline">{r.email}</a></div>
                    <div><span className="text-[#6e6e73]">{lang === "it" ? "Telefono" : "Phone"}:</span> <a href={`tel:${r.telefono.replace(/\s/g, "")}`} className="text-[#1D3F94] hover:underline">{r.telefono}</a></div>
                    {r.indirizzo && <div><span className="text-[#6e6e73]">{lang === "it" ? "Sede" : "Address"}:</span> {r.indirizzo}, {r.cap} {r.citta} ({r.provincia})</div>}
                  </div>
                  {r.note && (
                    <div className="mt-3 p-3 bg-white rounded-lg text-[12.5px] text-[#1d1d1f] border border-[#e4e4e7]">
                      {r.note}
                    </div>
                  )}
                  <div className="mt-4 flex items-center gap-3 flex-wrap">
                    <button
                      onClick={() => approveRequest(r.id)}
                      disabled={busy}
                      className="btn-pill btn-primary text-[13px] disabled:opacity-60"
                      data-testid={`approve-${r.id}`}
                    >
                      {lang === "it" ? "Approva" : "Approve"}
                    </button>
                    <button
                      onClick={() => rejectRequest(r.id)}
                      disabled={busy}
                      className="text-[13px] text-[#6e6e73] hover:text-[#e11d48] transition-colors"
                      data-testid={`reject-${r.id}`}
                    >
                      {lang === "it" ? "Rifiuta" : "Reject"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

    </>
  );
}
